<template>
  <div class="main-screen-root">
    <withdraw-to-card-skeleton v-if="!isInitialized" />

    <form
      v-else
      ref="form"
      class="e-form withdraw-card-form"
      autocomplete="off"
      @submit="() => {}"
    >
      {{ errors }}
      <input-coin-amount
        name="coinAmount"
        :model-value="coinAmount"
        title="WithdrawToCard | coinAmountInputTitle"
        :coin-id="coinId"
        :coin-list="availableCoins"
        :balance="selectedCoinBalance"
        :rules="coinAmountValidationRules"
        :limit-max="coinAmountMaxLimit"
        show-max-button
        limit-icon
        data-cy="withdraw-to-card-coin-amount-input"
        @update:modelValue="(value) => $emit('change-coin-amount', value)"
        @update:coin="(value) => $emit('change-coin', value)"
        @focus="$emit('focus', 'from')"
      >
        <template #popoverWithLimits>
          <popover-with-limits
            :transaction-limit-min="+activeCoinLimits?.min"
            :transaction-limit-max="+activeCoinLimits?.max"
            :day-limit-full="+activeCoinLimits?.dailyDefault"
            :day-limit-left="+activeCoinLimits?.daily"
            :month-limit-full="+activeCoinLimits?.monthlyDefault"
            :month-limit-left="+activeCoinLimits?.monthly"
            :fiat="selectedFiat"
          />
        </template>
      </input-coin-amount>

      <input-fiat-amount
        name="fiatAmount"
        class="fiat-amount-block"
        :model-value="fiatAmount"
        title="WithdrawToCard | fiatAmountInputTitle"
        :fiat-id="fiatId"
        :available-fiats="availableFiats"
        :rules="fiatAmountValidationRules"
        :limit-max="coinAmountMaxLimit"
        data-cy="withdraw-to-card-fiat-amount-input"
        @update:modelValue="(value) => $emit('change-fiat-amount', value)"
        @update:fiat="(value) => $emit('change-fiat', value)"
        @focus="$emit('focus', 'to')"
      />

      <chips-fiat
        class="chips-fiat"
        :is-initialized="isInitialized"
        :fiat="selectedFiat"
        data-cy="fiat-chips"
        @chip-selected="(value) => $emit('chip-selected', value)"
      />

      <base-decoration-line class="divider-before-payment-method-block" />

      <div class="payment-method-block">
        <select-payment-method
          class="payment-method-select"
          :model-value="paymentMethodId"
          :options="availablePaymentMethods"
          @update:modelValue="(value) => $emit('change-payment-method', value)"
        />

        <select-bank
          v-if="fiatId === 'RUB' && paymentMethodId === 'sbp'"
          class="select-bank-block"
          :model-value="bankId"
          :options="availableBanks"
          @update:modelValue="(value) => $emit('change-bank', value)"
        />

        <input-card-number
          v-else-if="fiatId === 'RUB'"
          class="card-number-block"
          :value="cardNumber"
          @input="(value) => $emit('change-card-number', value)"
        />

        <select-provider
          v-else
          class="provider-block"
          :model-value="providerId"
          :options="availableProviders"
          @update:modelValue="(value) => $emit('change-provider', value)"
        />
      </div>

      <input-phone
        v-if="paymentMethodId === 'sbp'"
        class="input-phone-block"
        :value="phoneNumber"
        @input="(value) => $emit('change-phone-number', value)"
      />

      <base-decoration-line class="divider-after-payment-method-block" />

      <withdraw-info
        class="info-block"
        :is-loading-rate="isLoadingRate"
        :rate="rate"
        :rate-time-to-live="rateTimeToLive"
        :coin="selectedCoin"
        :fiat="selectedFiat"
      />

      <terms-of-use-checkbox
        v-if="providerId === 'mercuryo'"
        class="terms-of-use"
        :value="isCheckedTermsOfUse"
        @input="(value) => $emit('check-terms-of-use', value)"
      />

      <div class="actions">
        <transition name="fade">
          <base-callout
            v-if="isInitialized && errorCode"
            class="callout"
            type="error"
            @close="$emit('clear-error')"
          >
            {{ $t(errorCode) || $t('Something went wrong, please try again') }}
          </base-callout>
        </transition>

        <base-button
          v-if="providerId === 'mercuryo'"
          type="cta"
          size="lg"
          data-cy="withdraw-button"
          :disabled="!!errors[0] || !isWithdrawAvailable"
          @onClick="$emit('submit-form')"
        >
          {{ $t('WithdrawToCard | continueToSelectCard') }}
        </base-button>
        <base-button
          v-else
          type="cta"
          size="lg"
          data-cy="withdraw-button"
          :disabled="!!errors[0] || !isWithdrawAvailable"
          @onClick="$emit('submit-form')"
        >
          {{
            $t('WithdrawToCard | continueToConfirmation', [
              fiatAmount ? formatFiatAmount(fiatAmount) : null,
            ])
          }}
        </base-button>
      </div>

      <div class="third-party-notice">
        <span>{{ $t('WithdrawToCard | thirdPartyNoticePlain') }}</span>
        <a
          class="third-party-link"
          href="https://emcd.io/static/terms_crypto_to_fiat_and_crypto_to_crypto.pdf"
          target="_blank"
        >
          {{ $t('WithdrawToCard | thirdPartyNoticeLink') }}
        </a>
      </div>
    </form>
  </div>
</template>

<script>
import Utils from '@emcd-shared/utils/formatters'
import WithdrawInfo from './WithdrawInfo.vue'
import ChipsFiat from './ChipsFiat.vue'
import WithdrawToCardSkeleton from './Skeleton.vue'
import TermsOfUseCheckbox from './TermsOfUseCheckbox.vue'
import PopoverWithLimits from './PopoverWithLimits.vue'
import BaseButton from '@/shared/ui/BaseButton/BaseButton.vue'
import { InputCoinAmount } from '@/features/InputCoinAmount'
import { InputFiatAmount } from '@/features/InputFiatAmount'
import { SelectPaymentMethod } from '@/features/SelectPaymentMethod'
import { SelectBank } from '@/features/SelectBank'
import { InputCardNumber } from '@/features/InputCardNumber'
import { SelectProvider } from '@/features/SelectProvider'
import { InputPhone } from '@/features/InputPhone'
import BaseDecorationLine from '@/shared/ui/BaseDecorationLine/BaseDecorationLine.vue'
import BaseCallout from '@/shared/ui/BaseCallout/BaseCallout.vue'

export default {
  components: {
    BaseCallout,
    PopoverWithLimits,
    InputPhone,
    SelectProvider,
    InputCardNumber,
    SelectBank,
    SelectPaymentMethod,
    InputFiatAmount,
    InputCoinAmount,
    BaseButton,
    BaseDecorationLine,
    WithdrawInfo,
    ChipsFiat,
    WithdrawToCardSkeleton,
    TermsOfUseCheckbox,
  },

  props: {
    isInitialized: Boolean,
    isLoadingRate: Boolean,
    isWithdrawAvailable: Boolean,
    coinAmount: String,
    coinId: String,
    availableCoins: Object,
    selectedCoinBalance: Number,
    coinAmountValidationRules: String,
    coinAmountMaxLimit: Number,
    fiatAmount: String,
    fiatId: String,
    availableFiats: Array,
    fiatAmountValidationRules: String,
    selectedFiat: Object,
    paymentMethodId: String,
    availablePaymentMethods: Array,
    bankId: String,
    availableBanks: Object,
    cardNumber: String,
    providerId: String,
    availableProviders: Array,
    phoneNumber: String,
    rate: Number,
    rateTimeToLive: Number,
    selectedCoin: Object,
    activeCoinLimits: Object,
    isCheckedTermsOfUse: Boolean,
    errorCode: String,
  },

  methods: {
    formatFiatAmount(value) {
      return Utils.formatCurrency(
        value,
        this.selectedFiat.value,
        2,
        false,
        'floor',
        {},
        this.$i18n.locale
      )
    },
  },
}
</script>

<style scoped lang="scss">
.fiat-amount-block {
  margin-top: 5px;
}

.chips-fiat {
  margin-top: 15px;
}

.divider-before-payment-method-block {
  margin-top: 30px;

  @media (width >= 512px) {
    display: none;
  }
}

.divider-after-payment-method-block {
  margin-top: 30px;

  @media (width >= 512px) {
    margin-top: 40px;
  }
}

.payment-method-block {
  margin-top: 40px;
  display: flex;
  align-items: flex-start;
  gap: 10px;

  @media (width < 512px) {
    margin-top: 30px;
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }
}

.payment-method-select {
  flex-basis: 210px;
  margin-bottom: 18px; /* Костыль - чтобы сообщение об ошибке у соседнего base input с номером карты не меняло высоту всего блока */

  @media (width < 512px) {
    flex-basis: unset;
    margin-bottom: unset;
  }
}

.card-number-block,
.select-bank-block,
.provider-block {
  flex-grow: 1;

  @media (width < 512px) {
    height: calc(
      56px + 18px
    ); /* Костыль - чтобы сообщение об ошибке не меняло высоту всего блока */
  }
}

.input-phone-block {
  margin-top: 20px;
  height: calc(
    56px + 18px
  ); /* Костыль - чтобы сообщение об ошибке не меняло высоту всего блока */
}

.info-block {
  margin-top: 30px;

  @media (width >= 512px) {
    margin-top: 40px;
  }
}

.terms-of-use {
  margin-top: 40px;
}

.third-party-notice {
  margin-top: 20px;
  color: var(--text-5);
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 18px */
}

.third-party-link {
  color: var(--text-4);
}

.actions {
  margin-top: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.callout {
  position: absolute;
  top: -60px;
  left: 0;
  right: 0;
  margin: 0 auto;
  z-index: 1;
}
</style>
